/**
 * useTtsPreview — Web Speech API TTS for segment preview
 * 
 * Provides free, instant, offline-capable TTS preview using the browser's
 * built-in SpeechSynthesis API. Each segment can be spoken individually
 * or all can be generated sequentially.
 * 
 * This is the "preview" layer. Gemini/Orbus voices will be added as a
 * separate provider that produces audio blobs for server-side rendering.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

export interface TtsVoiceOption {
  id: string;        // voiceURI
  name: string;      // display name
  lang: string;      // e.g. "pt-BR"
  group: string;     // optgroup label
  isDefault: boolean;
}

export type TtsAudioSource = 'original' | 'tts-preview' | 'gemini';

export interface TtsSegmentState {
  status: 'idle' | 'speaking' | 'done';
  textHash: string;  // to detect if text changed since last generation
}

function hashText(text: string): string {
  // Simple hash for change detection
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = ((h << 5) - h + text.charCodeAt(i)) | 0;
  }
  return String(h);
}

function classifyVoice(v: SpeechSynthesisVoice): string {
  const n = v.name.toLowerCase();
  if (n.startsWith('google')) return 'Google';
  if (n.startsWith('microsoft')) return 'Microsoft';
  if (v.lang.startsWith('pt')) return 'Português';
  return 'Outros';
}

export function useTtsPreview() {
  const [voices, setVoices] = useState<TtsVoiceOption[]>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('');
  const [rate, setRate] = useState(1.0);
  const [pitch, setPitch] = useState(1.0);
  const [speakingSegIndex, setSpeakingSegIndex] = useState<number | null>(null);
  const [segmentStates, setSegmentStates] = useState<Map<number, TtsSegmentState>>(new Map());
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const cancelRef = useRef(false);
  // Ref to avoid stale closure in async speakAll loop
  const segmentStatesRef = useRef(segmentStates);

  // Load voices — runs once on mount + when browser loads voices async
  const initialVoiceSelectedRef = useRef(false);
  useEffect(() => {
    const loadVoices = () => {
      const synth = window.speechSynthesis;
      const available = synth.getVoices();

      // Only Portuguese voices, grouped by provider
      const groupOrder: Record<string, number> = { 'Google': 0, 'Microsoft': 1, 'Outros': 2 };
      const mapped: TtsVoiceOption[] = available
        .filter(v => v.lang.startsWith('pt'))
        .map(v => {
          const n = v.name.toLowerCase();
          const group = n.startsWith('google') ? 'Google'
            : n.startsWith('microsoft') ? 'Microsoft'
            : 'Outros';
          return {
            id: v.voiceURI,
            name: v.name,
            lang: v.lang,
            group,
            isDefault: v.default
          };
        })
        .sort((a, b) => {
          const ga = groupOrder[a.group] ?? 99;
          const gb = groupOrder[b.group] ?? 99;
          if (ga !== gb) return ga - gb;
          // pt-BR before pt-PT
          const aPt = a.lang === 'pt-BR' ? 0 : 1;
          const bPt = b.lang === 'pt-BR' ? 0 : 1;
          if (aPt !== bPt) return aPt - bPt;
          return a.name.localeCompare(b.name);
        });

      setVoices(mapped);

      // Auto-select first pt-BR voice or default (only once)
      if (!initialVoiceSelectedRef.current && mapped.length > 0) {
        initialVoiceSelectedRef.current = true;
        const ptBr = mapped.find(v => v.lang === 'pt-BR');
        const def = mapped.find(v => v.isDefault);
        setSelectedVoiceId((ptBr || def || mapped[0]).id);
      }
    };

    if (!window.speechSynthesis) {
      console.warn('[useTtsPreview] speechSynthesis not supported in this browser');
      return;
    }

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []); // No deps — voice loading is fire-and-forget on mount

  // Keep ref in sync with state for async loops
  useEffect(() => {
    segmentStatesRef.current = segmentStates;
  }, [segmentStates]);

  // Get native voice object by ID
  const getVoice = useCallback((): SpeechSynthesisVoice | null => {
    const synth = window.speechSynthesis;
    const available = synth.getVoices();
    return available.find(v => v.voiceURI === selectedVoiceId) || null;
  }, [selectedVoiceId]);

  // Speak a single segment
  const speakSegment = useCallback((index: number, text: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!window.speechSynthesis) {
        console.warn('[useTtsPreview] speechSynthesis not available — skipping');
        resolve();
        return;
      }
      const synth = window.speechSynthesis;
      synth.cancel(); // stop any current speech

      if (!text.trim()) {
        resolve();
        return;
      }

      const utterance = new SpeechSynthesisUtterance(text);
      const voice = getVoice();
      if (voice) utterance.voice = voice;
      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.lang = 'pt-BR';

      setSpeakingSegIndex(index);
      setSegmentStates(prev => {
        const next = new Map(prev);
        next.set(index, { status: 'speaking', textHash: hashText(text) });
        return next;
      });

      utterance.onend = () => {
        setSpeakingSegIndex(null);
        setSegmentStates(prev => {
          const next = new Map(prev);
          next.set(index, { status: 'done', textHash: hashText(text) });
          return next;
        });
        resolve();
      };

      utterance.onerror = (e) => {
        setSpeakingSegIndex(null);
        setSegmentStates(prev => {
          const next = new Map(prev);
          next.set(index, { status: 'idle', textHash: hashText(text) });
          return next;
        });
        reject(e);
      };

      synth.speak(utterance);
    });
  }, [getVoice, rate, pitch]);

  // Speak all segments sequentially
  const speakAll = useCallback(async (segments: Array<{ index: number; text: string }>) => {
    cancelRef.current = false;
    setIsGeneratingAll(true);

    for (const seg of segments) {
      if (cancelRef.current) break;

      const currentState = segmentStatesRef.current.get(seg.index);
      const currentHash = hashText(seg.text);

      // Skip if already generated and text hasn't changed
      if (currentState?.status === 'done' && currentState.textHash === currentHash) {
        continue;
      }

      try {
        await speakSegment(seg.index, seg.text);
      } catch {
        // continue with next segment
      }

      // Small gap between segments
      if (!cancelRef.current) {
        await new Promise(r => setTimeout(r, 200));
      }
    }

    setIsGeneratingAll(false);
  }, [speakSegment]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    cancelRef.current = true;
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setSpeakingSegIndex(null);
    setIsGeneratingAll(false);
  }, []);

  // Check if a segment needs regeneration (text changed)
  const needsRegeneration = useCallback((index: number, text: string): boolean => {
    const state = segmentStates.get(index);
    if (!state || state.status === 'idle') return true;
    return state.textHash !== hashText(text);
  }, [segmentStates]);

  // Get segment TTS status
  const getSegmentStatus = useCallback((index: number): TtsSegmentState['status'] => {
    return segmentStates.get(index)?.status || 'idle';
  }, [segmentStates]);

  return {
    // Voice management
    voices,
    selectedVoiceId,
    setSelectedVoiceId,
    rate,
    setRate,
    pitch,
    setPitch,
    // Speech actions
    speakSegment,
    speakAll,
    stopSpeaking,
    // State
    speakingSegIndex,
    isGeneratingAll,
    needsRegeneration,
    getSegmentStatus,
  };
}
